import { useCallback, useEffect, useState } from "react";
import { hentUleste, kvitterUlest, merkVist, type Ulest } from "../state/session";

/**
 * «Du er invitert til …»
 *
 * ========  HVORFOR DEN SPØR, OG IKKE BARE BYTTER  ========
 *
 * Invitasjonen er bundet i det du logger inn — den trenger ingen
 * godkjenning, og nøklene kom med invitasjonen. Men å KASTE deg inn i en
 * annen produksjon er noe annet enn å gi deg tilgang til den. Står du i
 * privat med en halvskrevet logg, eller midt i en annen opptaksdag, er
 * et automatisk bytte det verste som kan skje.
 *
 * Derfor to knapper, og «bli værende» er den trygge.
 *
 * ========  DEN FORSVINNER IKKE AV SEG SELV  ========
 *
 * Velger du å bli, blir invitasjonen stående som ulest, og menyen bærer
 * et merke med antallet. Et varsel som bare vises én gang er et varsel
 * man mister mens telefonen ligger i lomma på vei til sett.
 *
 * Dialogen merker seg derfor bare som VIST. Merket kvitteres først når
 * man faktisk går inn i produksjonen — se `byttTilProduksjon`. Fram til
 * 01.11.2026 kvitterte begge knappene, og da forsvant merket i samme
 * trykk som man ba om å få bli værende.
 */
export function Invitasjon({ paaVelg }: {
  /** Bytt til produksjonen. Lukker dialogen. */
  paaVelg(produksjonId: string): void;
}): JSX.Element | null {
  const [koe, setKoe] = useState<readonly Ulest[]>([]);

  /* BARE DE SOM IKKE ER VIST. Resten staar igjen for merket i menyen. */
  useEffect(() => { setKoe(hentUleste().filter((u) => u.vist !== true)); }, []);

  /* Én om gangen. To dialoger oppaa hverandre er ikke et valg, det er
     et hinder — og den som er invitert til to produksjoner samtidig er
     uansett ikke hjulpet av at begge roper. */
  const naa = koe[0] ?? null;

  const lesInn = (): void =>
    setKoe(hentUleste().filter((u) => u.vist !== true));

  const bliVaerende = useCallback((): void => {
    if (naa === null) return;
    merkVist(naa.produksjonId, naa.rolle);
    lesInn();
  }, [naa]);

  const gaaInn = useCallback((): void => {
    if (naa === null) return;
    /* KVITTERES HER OGSAA, ikke bare i byttTilProduksjon.
       En fagfunksjon man faar i produksjonen man alt STAAR i gir ikke
       noe bytte — `byttTilProduksjon` gaar ut med en gang naar id-en er
       den samme — og da ville merket blitt staaende for alltid. */
    kvitterUlest(naa.produksjonId);
    paaVelg(naa.produksjonId);
    lesInn();
  }, [naa, paaVelg]);

  if (naa === null) return null;

  return (
    <div className="invitt" role="dialog" aria-modal="true">
      <div className="invitt__teppe" aria-hidden="true" />
      <div className="invitt__boks">
        {/* TO BESKJEDER, SAMME FORM.
            Den ene er «du er med i noe nytt», den andre «du kan gjoere
            noe mer der du alt er». Begge endrer hva flata tilbyr deg,
            og begge fortjener aa bli sagt — brukeren 01.11.2026. */}
        <span className="invitt__tittel">
          {naa.slag === "uleverte" ? "Du er ikke lenger med"
            : naa.slag === "fagfunksjon" ? "Du har fått en fagfunksjon"
            : "Du er invitert"}
        </span>
        <p className="invitt__tekst">
          {naa.slag === "uleverte" ? (
            <>
              Du er tatt ut av produksjonen <strong>{naa.navn}</strong>.{" "}
              {naa.antall === 1 ? "Én loggpost" : `${naa.antall ?? 0} loggposter`}{" "}
              rakk aldri opp til serveren, og {naa.antall === 1 ? "den" : "de"}{" "}
              ligger fortsatt her. Resten av produksjonen er slettet fra
              telefonen.
            </>
          ) : naa.slag === "fagfunksjon" ? (
            <>
              Du er nå også {ROLLENAVN[naa.rolle] ?? naa.rolle} i produksjonen{" "}
              <strong>{naa.navn}</strong>.
            </>
          ) : (
            <>
              Du har blitt invitert til å delta i produksjonen{" "}
              <strong>{naa.navn}</strong> som {ROLLENAVN[naa.rolle] ?? naa.rolle}.
            </>
          )}
        </p>
        <button type="button" className="invitt__hoved" onClick={gaaInn}>
          {naa.slag === "uleverte" ? "Se postene" : `Gå til ${naa.navn}`}
        </button>
        <button type="button" className="invitt__sekundaer" onClick={bliVaerende}>
          Bli hvor jeg er
        </button>
        {koe.length < 2 ? null : (
          <span className="invitt__flere">
            {koe.length - 1} {koe.length === 2 ? "invitasjon" : "invitasjoner"} til
          </span>
        )}
      </div>
    </div>
  );
}

const ROLLENAVN: Readonly<Record<string, string>> = {
  prodleder: "produksjonsleder", produsent: "produsent", redaktor: "redaktør",
  regi: "regi", vj: "VJ", klipper: "klipper", crew: "crew",
  reporter: "reporter", tekniker: "tekniker", afoto: "A-foto",
  koordinator: "koordinator",
};
